/**
 * Value Segment Types
 *
 * Based on LogicAppsUX patterns for representing parameter values.
 * Value segments allow mixing literal text with dynamic tokens from
 * previous steps, parameters, variables, and expressions.
 */

// =============================================================================
// VALUE SEGMENT
// =============================================================================

/**
 * A segment of a parameter value.
 * Parameters can contain multiple segments mixing literal text and tokens.
 *
 * Otto uses JavaScript expression syntax with ${...} wrappers.
 *
 * Example: "Hello ${outputs('GetUser')['name']}, your order is ${variables('orderId')}"
 * Would be represented as:
 * [
 *   { type: 'literal', value: 'Hello ' },
 *   { type: 'token', token: { tokenType: 'outputs', ... }, value: "${outputs('GetUser')['name']}" },
 *   { type: 'literal', value: ', your order is ' },
 *   { type: 'token', token: { tokenType: 'variable', ... }, value: "${variables('orderId')}" }
 * ]
 *
 * Expression syntax:
 * - Expressions are wrapped in ${...}
 * - Use $$ to escape a literal dollar sign
 * - Functions: ${concat('a', 'b')}, ${trigger().body}, ${outputs('action')}
 * - Property access: ${body('action')['property']}, ${body('action').property}
 * - JavaScript operators: ${a + b}, ${condition ? 'yes' : 'no'}
 */
export interface ValueSegment {
  /** Unique identifier for this segment */
  id: string;
  /** Type of segment */
  type: ValueSegmentType;
  /** The raw string value (for display and serialization) */
  value: string;
  /** Token information (only present for TOKEN type) */
  token?: Token;
}

export const ValueSegmentType = {
  /** Plain text/literal value */
  LITERAL: 'literal',
  /** Dynamic token reference */
  TOKEN: 'token',
} as const;
export type ValueSegmentType = (typeof ValueSegmentType)[keyof typeof ValueSegmentType];

// =============================================================================
// TOKEN
// =============================================================================

/**
 * A dynamic token representing a reference to outputs, parameters, variables, or expressions.
 */
export interface Token {
  /** Action name that produces this token (for outputs) */
  actionName?: string;
  /** Array iteration details (for loop items) */
  arrayDetails?: ArrayDetails;
  /** Brand color for UI display */
  brandColor?: string;
  /** Child tokens for nested objects/arrays (used for tree display in token picker) */
  children?: Token[];
  /** Description of the token */
  description?: string;
  /** Expression AST (for FX tokens) */
  expression?: Expression;
  /** Format hint (e.g., 'datetime', 'email') */
  format?: string;
  /** Icon URL for UI display */
  icon?: string;
  /** Whether this token contains secure data */
  isSecure?: boolean;
  /** Unique key identifying this token */
  key: string;
  /** Token name (for display) */
  name?: string;
  /** Whether this token is required */
  required?: boolean;
  /** JSON Schema for the token value */
  schema?: SchemaObject;
  /** Source of the token (e.g., 'body', 'headers') */
  source?: string;
  /** Display title */
  title: string;
  /** Type of token */
  tokenType: TokenType;
  /** Data type (e.g., 'string', 'integer', 'object') */
  type?: string;
  /** The expression value (for serialization) */
  value?: string;
}

export const TokenType = {
  /** Expression/function token (fx) */
  FX: 'fx',
  /** Current loop item */
  ITEM: 'item',
  /** Current iteration index in a loop */
  ITERATIONINDEX: 'iterationIndex',
  /** Outputs from a previous action/trigger */
  OUTPUTS: 'outputs',
  /** Workflow parameter */
  PARAMETER: 'parameter',
  /** Workflow variable */
  VARIABLE: 'variable',
  /** Agent parameter */
  AGENTPARAMETER: 'agentParameter',
} as const;
export type TokenType = (typeof TokenType)[keyof typeof TokenType];

/**
 * Details about array context for items/iteration tokens
 */
export interface ArrayDetails {
  /** Source loop action name */
  loopSource?: string;
  /** Expression for the array source when an implicit ForEach can be created */
  implicitForeachExpression?: string;
  /** item() accessor path to the selected property inside the implicit ForEach */
  implicitItemPath?: string;
  /** Parent array name if nested */
  parentArrayName?: string;
  /** Parent array key if nested */
  parentArrayKey?: string;
  /** Schema of items in the array */
  itemSchema?: SchemaObject;
}

// =============================================================================
// EXPRESSION
// =============================================================================

/**
 * Parsed expression AST
 */
export interface Expression {
  /** Expression type */
  type: ExpressionType;
  /** Start position in the original string */
  startPosition: number;
  /** End position in the original string */
  endPosition: number;
  /** Original expression string */
  expression: string;
}

export const ExpressionType = {
  Function: 'Function',
  NullLiteral: 'NullLiteral',
  BooleanLiteral: 'BooleanLiteral',
  NumberLiteral: 'NumberLiteral',
  StringLiteral: 'StringLiteral',
  StringInterpolation: 'StringInterpolation',
} as const;
export type ExpressionType = (typeof ExpressionType)[keyof typeof ExpressionType];

/**
 * Function expression (e.g., concat(), outputs(), etc.)
 */
export interface ExpressionFunction extends Expression {
  type: typeof ExpressionType.Function;
  /** Function name */
  name: string;
  /** Function arguments */
  arguments: Expression[];
  /** Property dereferences (e.g., ['body']['name']) */
  dereferences: Dereference[];
}

// Type guard for ExpressionFunction
export function isExpressionFunction(expr: Expression): expr is ExpressionFunction {
  return expr.type === ExpressionType.Function && 'name' in expr;
}

/**
 * Literal expression (string, number, boolean, null)
 */
export interface ExpressionLiteral extends Expression {
  /** The literal value */
  value: string;
}

/**
 * Property dereference in an expression
 */
export interface Dereference {
  /** The dereference expression */
  expression: Expression;
  /** Whether this is a safe navigation (?.) */
  isSafe: boolean;
}

// =============================================================================
// OUTPUT SOURCES
// =============================================================================

export const OutputSource = {
  Body: 'body',
  Headers: 'headers',
  StatusCode: 'statusCode',
  Queries: 'queries',
  Outputs: 'outputs',
} as const;
export type OutputSource = (typeof OutputSource)[keyof typeof OutputSource];

export const OutputKeys = {
  Body: 'Body',
  Headers: 'Headers',
  StatusCode: 'Status code',
  Queries: 'Queries',
  Outputs: 'Outputs',
  Item: 'Current item',
} as const;

// =============================================================================
// SCHEMA (simplified OpenAPI Schema)
// =============================================================================

/**
 * Simplified JSON Schema / OpenAPI Schema object
 * Extended with Azure Logic Apps specific extensions
 */
export interface SchemaObject {
  type?: string;
  format?: string;
  title?: string;
  description?: string;
  properties?: Record<string, SchemaObject>;
  items?: SchemaObject;
  /**
   * Required fields. Can be:
   * - string[] at the object level listing required property names
   * - boolean at the property level indicating if this property is required
   */
  required?: string[] | boolean;
  enum?: unknown[];
  default?: unknown;
  additionalProperties?: boolean | SchemaObject;
  /** String constraints */
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  /** Numeric constraints */
  minimum?: number;
  maximum?: number;
  /**
   * Azure Logic Apps visibility extension
   * - 'important': Show in compact view
   * - 'advanced': Show only in expanded view
   * - 'internal': Hidden from UI, used in serialization
   * - 'hideInUI': Completely hidden (LogicAppsUX legacy)
   */
  'x-ms-visibility'?: 'advanced' | 'internal' | 'important' | 'hideInUI';
  /**
   * Azure Logic Apps display label extension. Preferred over `title` for
   * UI labels (parameter editors, token picker entries, etc.) per
   * LogicAppsUX convention.
   */
  'x-ms-summary'?: string;
  /** Azure Logic Apps dynamic data extension */
  'x-ms-dynamic'?: boolean;
  /** Custom editor specification */
  'x-ms-editor'?: string;
  /** Custom editor options */
  'x-ms-editor-options'?: Record<string, unknown>;
  /** Input dependency rules */
  'x-ms-input-dependencies'?: unknown;
  /** Dynamic properties */
  'x-ms-dynamic-properties'?: unknown;
  /** Serialization rules */
  'x-ms-serialization'?: unknown;
  /** Deserialization rules */
  'x-ms-deserialization'?: unknown;
  /** Dynamic list configuration */
  'x-ms-dynamic-list'?: unknown;
  /** Dynamic schema configuration (for generating additional parameters) */
  'x-ms-dynamic-schema'?: unknown;
  /** Dynamic tree configuration (for file/folder pickers) */
  'x-ms-dynamic-tree'?: unknown;
  /**
   * Otto-only extension. Records the body sub-path for a dynamic-schema parent
   * so it survives the `parametersToSchema` → `schemaPropertyToParameter`
   * manifest round-trip and round-trips back into `ParameterInfo.info.bodyKeyPath`.
   * Empty string = body root (e.g. SharePoint PostItem); non-empty = sub-path
   * (e.g. SQL ExecuteProcedure_V2's `parameters`).
   */
  'x-otto-body-key-path'?: string;
  /**
   * Otto-only extension. Marks a primitive/dictionary body parameter whose
   * value IS the entire request body. Preserves
   * `ParameterInfo.info.isBodyRoot` across the manifest round-trip.
   */
  'x-otto-body-root'?: boolean;
  /**
   * Whether this parameter requires a connection to be selected before its
   * options or dynamic data can be resolved. Used by Azure Logic Apps connector
   * parameters that pull values (e.g., model deployments) from the connection.
   */
  'x-ms-connection-required'?: boolean;
  /**
   * Connection-driven option lookup configuration. Used together with
   * `x-ms-connection-required` to populate combobox/dropdown values from the
   * selected connection (e.g., AI model deployments listed by the agent
   * connection).
   */
  'x-ms-connection-options'?: Record<string, unknown>;
  /**
   * Indicates that the parameter editor should expose a "Create new" affordance
   * (e.g., create a new deployment / resource) alongside the existing options.
   */
  'x-ms-create-new-resource'?: boolean;
  /**
   * Side-effect mapping applied when the user picks an option from this
   * parameter (combobox with x-ms-dynamic-list). Maps target parameter keys
   * to dot-paths into the selected option's `data` payload.
   * See services/onSelectMapping.ts.
   */
  'x-ms-on-select'?: Record<string, string>;
  /** Whether this is a node ID field */
  'x-ms-is-node-id'?: boolean;
  /** Schema definitions for $ref resolution */
  definitions?: Record<string, SchemaObject>;
}

/**
 * Check if a property is required in a schema.
 * Handles both parent-level required array and property-level required boolean.
 *
 * @param parentSchema - The parent schema object (for checking required array)
 * @param propertyName - The name of the property to check
 * @param propertySchema - The property's schema (for checking required boolean)
 * @returns Whether the property is required
 */
export function isPropertyRequired(
  parentSchema: SchemaObject | undefined,
  propertyName: string,
  propertySchema?: SchemaObject
): boolean {
  // Check property-level required (boolean)
  if (propertySchema?.required === true) {
    return true;
  }
  // Check parent-level required array
  if (Array.isArray(parentSchema?.required)) {
    return parentSchema.required.includes(propertyName);
  }
  return false;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Generate a unique ID for a value segment
 */
export function generateSegmentId(): string {
  return `seg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Create a literal value segment
 */
export function createLiteralValueSegment(value: string, id?: string): ValueSegment {
  return {
    id: id || generateSegmentId(),
    type: ValueSegmentType.LITERAL,
    value,
  };
}

/**
 * Create a token value segment
 */
export function createTokenValueSegment(token: Token, value: string): ValueSegment {
  return {
    id: generateSegmentId(),
    type: ValueSegmentType.TOKEN,
    token,
    value,
  };
}

/**
 * Check if a segment is a literal
 */
export function isLiteralSegment(segment: ValueSegment): boolean {
  return segment.type === ValueSegmentType.LITERAL;
}

/**
 * Check if a segment is a token
 */
export function isTokenSegment(segment: ValueSegment): boolean {
  return segment.type === ValueSegmentType.TOKEN;
}

/**
 * Check if a token is an expression (fx)
 */
export function isExpressionToken(token: Token): boolean {
  return token.tokenType === TokenType.FX;
}

/**
 * Check if a token is an output token
 */
export function isOutputToken(token: Token): boolean {
  return token.tokenType === TokenType.OUTPUTS;
}

/**
 * Check if a token is a variable token
 */
export function isVariableToken(token: Token): boolean {
  return token.tokenType === TokenType.VARIABLE;
}

/**
 * Check if a token is a parameter token
 */
export function isParameterToken(token: Token): boolean {
  return token.tokenType === TokenType.PARAMETER;
}

/**
 * Convert value segments to a plain string (for serialization)
 */
export function segmentsToString(segments: ValueSegment[]): string {
  return segments.map((s) => s.value).join('');
}

/**
 * Check if segments contain any tokens
 */
export function hasTokens(segments: ValueSegment[]): boolean {
  return segments.some((s) => s.type === ValueSegmentType.TOKEN);
}

/**
 * Get all tokens from segments
 */
export function getTokensFromSegments(segments: ValueSegment[]): Token[] {
  return segments
    .filter((s): s is ValueSegment & { token: Token } => s.type === ValueSegmentType.TOKEN && !!s.token)
    .map((s) => s.token);
}

/**
 * Type guard to check if a value is a ValueSegment array
 */
export function isValueSegmentArray(value: unknown): value is ValueSegment[] {
  if (!Array.isArray(value)) return false;
  if (value.length === 0) return false;
  // Check that each item looks like a ValueSegment
  return value.every(
    (item) =>
      item &&
      typeof item === 'object' &&
      'id' in item &&
      'type' in item &&
      'value' in item &&
      (item.type === ValueSegmentType.LITERAL || item.type === ValueSegmentType.TOKEN)
  );
}

// =============================================================================
// TOKEN DISPLAY HELPERS
// =============================================================================

/** Default brand colors for token types */
export const TokenBrandColors = {
  [TokenType.FX]: '#ad008c',
  [TokenType.PARAMETER]: '#0078d4',
  [TokenType.VARIABLE]: '#00758f',
  [TokenType.OUTPUTS]: '#0078d4',
  [TokenType.ITEM]: '#00758f',
  [TokenType.ITERATIONINDEX]: '#00758f',
  [TokenType.AGENTPARAMETER]: '#7719aa',
} as const;

/** Default icons for token types (data URI or icon name) */
export const TokenIcons = {
  [TokenType.FX]: 'fx',
  [TokenType.PARAMETER]: 'parameter',
  [TokenType.VARIABLE]: 'variable',
  [TokenType.OUTPUTS]: 'output',
  [TokenType.ITEM]: 'item',
  [TokenType.ITERATIONINDEX]: 'index',
  [TokenType.AGENTPARAMETER]: 'agent',
} as const;
